import BaseWrapper from "../../../components/ui/BaseWrapper";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useQuery } from "@tanstack/react-query";
import { fetchAllClients } from "../../../services/api/client";
import { useRef, useState } from "react";
import { debounce } from "../../../utils/helpers";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
const CreateEditProjectForm = ({ formik, isEditable, isEditMode }) => {
  const [queryParams, setQueryParams] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", queryParams],
    queryFn: () => fetchAllClients({ search: queryParams, isParentClient: false }),
  });

  const handleSearchChange = (_event, newInputValue, reason) => {
    if (reason !== "input") return;

    const inputValue = (newInputValue || "").trim();
    setSearchInput(inputValue);
    debouncedSearch(inputValue);
  };

  const debouncedSearch = useRef(
    debounce((query) => {
      setQueryParams(query);
    }, 500),
  ).current;

  const formattedClientData = clientsData?.data?.data?.map((client) => ({
    label: client.clientName,
    value: client.id,
  }));

  return (
    <BaseWrapper>
      <div className="pb-8 ">
        <form onSubmit={formik.handleSubmit} className="grid grid-cols-3 gap-5 mt-7 text-black">
          <div className="lg:col-span-1 col-span-3">
            <BaseAutoSelect
              fullHeight={false}
              fullWidth
              labelShrink={false}
              id="clientId"
              name="clientId"
              label="Select Client"
              options={formattedClientData}
              required={true}
              value={formik?.values?.clientId}
              onChange={formik?.handleChange}
              onInputChange={handleSearchChange}
              onBlur={formik.handleBlur}
              error={formik.touched.clientId && formik.errors.clientId}
              helperText={formik.touched.clientId && formik.errors.clientId}
              disabled={!isEditable && isEditMode}
            />
          </div>
          <div className="lg:col-span-1 col-span-3">
            <BaseTextField
              fullHeight={false}
              fullWidth
              labelShrink={false}
              isDefaultField={false}
              id="title"
              name="title"
              label="Project Name"
              optional={false}
              value={formik?.values?.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && formik.errors.title}
              helperText={formik.touched.title && formik.errors.title}
              disabled={!isEditable && isEditMode}
            />
          </div>

          <div className="lg:col-span-1 col-span-3">
            <label
              htmlFor="projectContractFile"
              className="block text-sm font-sm mb-1 text-[#505050]"
            >
              Contract
            </label>

            {/* File upload UI */}
            <div
              className={`
              relative w-full border rounded shadow-sm shadow-gray-300 p-[10px] pr-12 
              text-sm text-gray-700 font-light 
              ${!isEditable && isEditMode ? "bg-[#F6F6F6] text-gray-500 cursor-not-allowed" : "bg-white cursor-pointer"}
            `}
            >
              <span>
                {(formik.values?.projectContractFile?.url && (
                  <a
                    href={formik.values.projectContractFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-1 cursor-pointer block text-blue-500 hover:underline font-300"
                  >
                    {formik.values?.projectContractFile?.fileName}
                  </a>
                )) ||
                  formik.values?.projectContractFile?.name ||
                  "Choose File"}
              </span>
              <i
                onClick={() => document.getElementById("projectContractInput").click()}
                className="fa-solid fa-paperclip text-primary text-xl absolute right-3 top-1/2 -translate-y-1/2"
              />

              <input
                type="file"
                id="projectContractInput"
                name="projectContractFile"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files[0];
                  formik.setFieldValue("projectContractFile", file);
                }}
                onBlur={formik.handleBlur}
                className="hidden"
                disabled={!isEditable && isEditMode}
              />
            </div>
          </div>
          <div className="lg:col-span-1 col-span-3">
            <BaseTextField
              fullHeight={false}
              fullWidth
              labelShrink={false}
              optional={false}
              isDefaultField={false}
              id="managerName"
              name="managerName"
              label="Project Manager"
              value={formik.values?.managerName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              // inputProps={{ maxLength: 30 }}
              error={formik.touched.managerName && formik.errors.managerName}
              helperText={formik.touched.managerName && formik.errors.managerName}
              disabled={!isEditable && isEditMode}
            />
          </div>
          <div className="lg:col-span-1 col-span-3">
            <label htmlFor="phoneNumber" className="block text-sm font-sm mb-1 text-[#505050]">
              Phone
            </label>

            <div className="relative w-full">
              <PhoneInput
                inputClass={`!w-full !h-[42px] !pl-12 !pr-3 !text-sm !font-light 
                !text-black !border !rounded-md !shadow-md 
                ${!isEditable && isEditMode ? "!bg-[#F6F6F6] !cursor-not-allowed !text-gray-500" : "!bg-white"}
                ${formik.touched.phone && formik.errors.phone && "!border-gray-300"} 
                focus:!outline-none focus:!ring-1 focus:!ring-orange-400`}
                buttonClass="!border-none !bg-transparent"
                containerClass="!w-full"
                specialLabel=""
                country={"au"}
                value={formik.values.phoneNumber}
                onChange={(value) => formik.setFieldValue("phoneNumber", value)}
                onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                inputProps={{
                  name: "phoneNumber",
                  id: "phoneNumber",
                  type: "tel",
                }}
                disabled={!isEditable && isEditMode}
              />

              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 col-span-3">
            <BaseTextField
              fullHeight={false}
              fullWidth
              labelShrink={false}
              id="email"
              name="email"
              label="Email"
              value={formik.values?.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="col-span-4"
              optional={false}
              isDefaultField={false}
              inputProps={{ maxLength: 50 }}
              error={formik.touched.email && formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              disabled={!isEditable && isEditMode}
            />
          </div>

          <div className="lg:col-span-1 col-span-3">
            <BaseCheckbox
              disabled={!isEditable && isEditMode}
              label="PO Required"
              checked={formik.values.isPoRequired}
              onChange={(e) => {
                formik.setFieldValue("isPoRequired", e.target.checked);
              }}
            />
          </div>

          <div className="col-span-3">
            <p className="font-normal ">Project's Policies</p>
            <textarea
              name="policies"
              value={formik.values?.policies}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.policies && formik.errors.policies}
              helperText={formik.touched.policies && formik.errors.policies}
              className="
              bg-white border border-gray-300 w-full rounded-md h-32 resize-none p-4 mt-1 
              focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300] shadow-md
              disabled:bg-[#F6F6F6] disabled:text-gray-500 disabled:cursor-not-allowed"
              disabled={!isEditable && isEditMode}
            ></textarea>
            {formik.touched.policies && formik.errors.policies && (
              <p className="text-[#E20000] text-xs mt-1">{formik.errors.policies}</p>
            )}
          </div>

          {/* Row 7 */}
          <div className="col-span-3">
            <p className="font-normal ">Project's Instructions</p>
            <textarea
              name="instructions"
              value={formik.values?.instructions}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="
              bg-white border border-gray-300 w-full rounded-md h-32 resize-none p-4 mt-1 
              focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300] shadow-md
              disabled:bg-[#F6F6F6] disabled:text-gray-500 disabled:cursor-not-allowed"
              disabled={!isEditable && isEditMode}
            ></textarea>
            {formik.touched.instructions && formik.errors.instructions && (
              <p className="text-[#E20000] text-xs mt-1">{formik.errors.instructions}</p>
            )}
          </div>
        </form>
      </div>
    </BaseWrapper>
  );
};

export default CreateEditProjectForm;
